/**
 * MatchAnalysisLoader — Pré-animation avant l'apparition du carrousel.
 *
 * Affiche en cascade :
 *  - Analyse des compétences...
 *  - Analyse de la spécialité...
 *  - Analyse de la localisation...
 *  - Recherche des spécialistes disponibles...
 *
 * Durée totale ≈ 2.5s. À utiliser conjointement avec ExpertCarousel.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";

const STEPS = [
  "Analyse des compétences",
  "Analyse de la spécialité",
  "Analyse de la localisation",
  "Recherche des spécialistes disponibles",
];

export default function MatchAnalysisLoader({ onComplete, durationPerStepMs = 600 }) {
  const [done, setDone] = useState(0);

  useEffect(() => {
    if (done >= STEPS.length) {
      const t = setTimeout(() => onComplete?.(), 350);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setDone((d) => d + 1), durationPerStepMs);
    return () => clearTimeout(t);
  }, [done, durationPerStepMs, onComplete]);

  return (
    <div
      className="max-w-md mx-auto py-10 px-6 text-center"
      data-testid="match-analysis-loader"
      role="status"
      aria-live="polite"
    >
      <p className="text-[10px] uppercase tracking-[0.32em] text-copper mb-3">
        Confilex Match™ · Analyse en cours
      </p>
      <h3 className="font-serif italic text-2xl text-night mb-8">
        Identification des professionnels pertinents
      </h3>
      <ul className="space-y-3 text-left">
        {STEPS.map((label, i) => {
          const isDone = i < done;
          const isActive = i === done;
          return (
            <li
              key={label}
              data-testid={`analysis-step-${i}`}
              className={`flex items-center gap-3 px-3 py-2 rounded-sm border transition-all duration-300 ${
                isDone
                  ? "border-petrol/20 bg-petrol/5 text-night opacity-100"
                  : isActive
                  ? "border-copper/40 bg-paper text-night opacity-100"
                  : "border-sage/40 bg-ivory text-night/40 opacity-50"
              }`}
            >
              {isDone ? (
                <CheckCircle2 className="w-4 h-4 text-petrol shrink-0" />
              ) : isActive ? (
                <Loader2 className="w-4 h-4 text-copper shrink-0 animate-spin" />
              ) : (
                <div className="w-4 h-4 shrink-0 rounded-full border border-sage" />
              )}
              <span className="text-sm">{label}{isActive ? "…" : ""}</span>
            </li>
          );
        })}
      </ul>
      <p className="text-[11px] text-night/55 italic mt-6 leading-relaxed">
        Sélection objective sur critères de pertinence dossier ↔ profil.
        Aucune notation de performance n'est calculée.
      </p>
    </div>
  );
}
